/**
 * Per-year tax parameters.
 * - `tariff`: income tax formula of §32a Abs. 1 EStG (Grundtarif). Zone 2 and 3 use
 *   (a * y + b) * y [+ c] with y = (zvE - previous zone limit) / 10,000.
 * - `soliThreshold`: Freigrenze of §3 Abs. 3 SolZG, measured on the income tax itself.
 * - `soliMilderung`: above the Freigrenze the surcharge is capped at 11.9% of the excess (§4 SolZG).
 */
export const TAX_PRESETS = {
  2025: {
    abgeltungssteuer: 0.25,
    solidaritaetszuschlag: 0.055,
    abgeltungssteuerTotal: 0.26375,
    sparerPauschbetrag: { single: 1000, married: 2000 },
    soliThreshold: { single: 19950, married: 39900 },
    soliMilderung: 0.119,
    tariff: {
      grundfreibetrag: 12096,
      zone2: { upTo: 17443, a: 932.30, b: 1400 },
      zone3: { upTo: 68480, a: 176.64, b: 2397, c: 1015.13 },
      zone4: { upTo: 277825, rate: 0.42, minus: 10911.92 },
      zone5: { rate: 0.45, minus: 19246.67 },
    },
  },
  2026: {
    abgeltungssteuer: 0.25,
    solidaritaetszuschlag: 0.055,
    abgeltungssteuerTotal: 0.26375,
    sparerPauschbetrag: { single: 1000, married: 2000 },
    soliThreshold: { single: 20350, married: 40700 },
    soliMilderung: 0.119,
    tariff: {
      grundfreibetrag: 12348,
      zone2: { upTo: 17799, a: 914.51, b: 1400 },
      zone3: { upTo: 69878, a: 173.10, b: 2397, c: 1034.87 },
      zone4: { upTo: 277825, rate: 0.42, minus: 11135.63 },
      zone5: { rate: 0.45, minus: 19470.38 },
    },
  },
} as const;

export type TaxYear = keyof typeof TAX_PRESETS;

export enum CompoundingFrequency {
  Monthly = 12,
  Quarterly = 4,
  Annually = 1,
}

export const COMPOUND_RANGES = {
  initialInvestment:   { min: 0, max: 1_000_000, step: 100,   default: 10_000 },
  monthlyContribution: { min: 0, max: 10_000,    step: 50,    default: 200    },
  annualInterestRate:  { min: 0, max: 0.20,      step: 0.001, default: 0.07   },
  years:               { min: 1, max: 50,        step: 1,     default: 20     },
  inflationRate:       { min: 0, max: 0.10,      step: 0.001, default: 0.02   },
} as const;

export const RENTAL_RANGES = {
  // Property
  purchasePrice:              { min: 0,    max: 2_000_000, step: 1_000,  default: 300_000 },
  monthlyRent:                { min: 0,    max: 10_000,    step: 10,     default: 1_000   },
  buildingYear:               { min: 1900, max: 2030,      step: 1,      default: 2000    },
  // Running costs: total Hausgeld, the reserve inside it, and what the tenant pays back.
  // Non-recoverable costs (excl. reserve) = Hausgeld - reserve - tenant service charges.
  hausgeldMonthly:             { min: 0,    max: 2_000,     step: 5,      default: 280     },
  maintenanceReserveMonthly:   { min: 0,    max: 1_000,     step: 5,      default: 60      },
  tenantServiceChargesMonthly: { min: 0,    max: 2_000,     step: 5,      default: 160     },
  // Financing
  equity:                     { min: 0,    max: 2_000_000, step: 1_000,  default: 60_000  },
  interestRate:               { min: 0,    max: 0.10,      step: 0.0005, default: 0.035   },
  tilgungRate:                { min: 0,    max: 0.10,      step: 0.001,  default: 0.02    },
  // Buyer
  taxableIncome:              { min: 0,    max: 500_000,   step: 1_000,  default: 70_000  },
  // Advanced
  notaryRate:                 { min: 0,    max: 0.05,      step: 0.001,  default: 0.02    },
  brokerRate:                 { min: 0,    max: 0.08,      step: 0.0001, default: 0.0357  },
  buildingShare:              { min: 0.10, max: 1,         step: 0.01,   default: 0.80    },
  zinsbindungYears:           { min: 1,    max: 30,        step: 1,      default: 10      },
  rentGrowth:                 { min: 0,    max: 0.05,      step: 0.001,  default: 0.015   },
  valueGrowth:                { min: -0.02, max: 0.06,     step: 0.001,  default: 0.015   },
  vacancyRate:                { min: 0,    max: 0.15,      step: 0.005,  default: 0.02    },
  // A sale is only free of income tax after 10 years (§23 EStG), so the projection starts there.
  holdingYears:               { min: 10,   max: 30,        step: 1,      default: 10      },
} as const;

// AfA (building depreciation) and Kirchensteuer rates below are sourced from
// the official published law text: https://www.gesetze-im-internet.de/estg/__7.html (§7 EStG)

/** Linear building AfA rate per §7 Abs. 4 EStG, tiered by completion year. */
export function getLinearAfaRate(buildingYear: number): number {
  if (buildingYear < 1925) return 0.025;
  if (buildingYear <= 2022) return 0.02;
  return 0.03; // completed 2023 or later
}

/** Optional declining-balance AfA per §7 Abs. 5a EStG, 5% of remaining book value per year. */
export const DEGRESSIVE_AFA_RATE = 0.05;

/**
 * §7 Abs. 5a EStG applies to residential buildings with construction start between
 * 1 Oct 2023 and 30 Sept 2029. This app only collects a completion year, not the exact
 * construction-start date, so eligibility is approximated by completion year 2023–2030.
 */
export function isDegressiveAfaEligible(buildingYear: number): boolean {
  return buildingYear >= 2023 && buildingYear <= 2030;
}

export type BundeslandCode =
  | 'BW' | 'BY' | 'BE' | 'BB' | 'HB' | 'HH' | 'HE' | 'MV'
  | 'NI' | 'NW' | 'RP' | 'SL' | 'SN' | 'ST' | 'SH' | 'TH';

/**
 * Kirchensteuer is 8% in Bayern and Baden-Württemberg, 9% in the other 14 states.
 * Grunderwerbsteuer (real estate transfer tax) is set by each state; rates as of 2026
 * (last changes: Bremen 5.5% from 1 Jul 2025, Thüringen 5.0% from 1 Jan 2024).
 */
export const BUNDESLAENDER: {
  code: BundeslandCode;
  name: string;
  kirchensteuerRate: 0.08 | 0.09;
  grunderwerbsteuerRate: number;
}[] = [
  { code: 'BW', name: 'Baden-Württemberg',       kirchensteuerRate: 0.08, grunderwerbsteuerRate: 0.050 },
  { code: 'BY', name: 'Bayern',                  kirchensteuerRate: 0.08, grunderwerbsteuerRate: 0.035 },
  { code: 'BE', name: 'Berlin',                  kirchensteuerRate: 0.09, grunderwerbsteuerRate: 0.060 },
  { code: 'BB', name: 'Brandenburg',             kirchensteuerRate: 0.09, grunderwerbsteuerRate: 0.065 },
  { code: 'HB', name: 'Bremen',                  kirchensteuerRate: 0.09, grunderwerbsteuerRate: 0.055 },
  { code: 'HH', name: 'Hamburg',                 kirchensteuerRate: 0.09, grunderwerbsteuerRate: 0.055 },
  { code: 'HE', name: 'Hessen',                  kirchensteuerRate: 0.09, grunderwerbsteuerRate: 0.060 },
  { code: 'MV', name: 'Mecklenburg-Vorpommern',  kirchensteuerRate: 0.09, grunderwerbsteuerRate: 0.060 },
  { code: 'NI', name: 'Niedersachsen',           kirchensteuerRate: 0.09, grunderwerbsteuerRate: 0.050 },
  { code: 'NW', name: 'Nordrhein-Westfalen',     kirchensteuerRate: 0.09, grunderwerbsteuerRate: 0.065 },
  { code: 'RP', name: 'Rheinland-Pfalz',         kirchensteuerRate: 0.09, grunderwerbsteuerRate: 0.050 },
  { code: 'SL', name: 'Saarland',                kirchensteuerRate: 0.09, grunderwerbsteuerRate: 0.065 },
  { code: 'SN', name: 'Sachsen',                 kirchensteuerRate: 0.09, grunderwerbsteuerRate: 0.055 },
  { code: 'ST', name: 'Sachsen-Anhalt',          kirchensteuerRate: 0.09, grunderwerbsteuerRate: 0.050 },
  { code: 'SH', name: 'Schleswig-Holstein',      kirchensteuerRate: 0.09, grunderwerbsteuerRate: 0.065 },
  { code: 'TH', name: 'Thüringen',               kirchensteuerRate: 0.09, grunderwerbsteuerRate: 0.050 },
];

export function getKirchensteuerRateForBundesland(code: BundeslandCode): 0.08 | 0.09 {
  return BUNDESLAENDER.find((b) => b.code === code)?.kirchensteuerRate ?? 0.09;
}

export function getGrunderwerbsteuerRateForBundesland(code: BundeslandCode): number {
  return BUNDESLAENDER.find((b) => b.code === code)?.grunderwerbsteuerRate ?? 0.065;
}
